import { useEffect, useRef, useState } from 'react';
import { createWorker, PSM } from 'tesseract.js';

type Props = {
  value: string;
  onChange: (patente: string) => void;
  onPhoto?: (blob: Blob) => void;
  onDeletePhoto?: () => void;
};

type TWorker = Awaited<ReturnType<typeof createWorker>>;

const WHITELIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const TIMEOUT_MS = 35_000;

// Formatos de patente argentinos, en orden de prioridad
const PATRONES: RegExp[] = [
  /[A-Z]{2}\d{3}[A-Z]{2}/, // Mercosur auto: AB123CD
  /[A-Z]\d{3}[A-Z]{3}/,    // Mercosur moto: A123BCD
  /[A-Z]{3}\d{3}/,         // Formato viejo auto: ABC123
  /\d{3}[A-Z]{3}/,         // Formato viejo moto: 123ABC
];

function extraerPatente(texto: string): string | null {
  // La chapa tiene espacios (AB 123 CD) — se colapsa todo antes de buscar
  const s = texto.toUpperCase().replace(/[^A-Z0-9]/g, '');
  for (const re of PATRONES) {
    const m = s.match(re);
    if (m) return m[0];
  }
  return null;
}

// ---------- Worker singleton (se reusa entre escaneos; bajar el motor cuesta ~9MB) ----------

let workerPromise: Promise<TWorker> | null = null;
let statusListener: ((m: { status: string; progress?: number }) => void) | null = null;

function crearWorker(): Promise<TWorker> {
  // Rutas absolutas — necesario para que el Worker las resuelva correctamente
  const base = (import.meta.env.BASE_URL as string).replace(/\/$/, '');
  const origin = window.location.origin;
  return createWorker('eng', 1, {
    workerBlobURL: false,
    workerPath: `${origin}${base}/tesseract/worker.min.js`,
    langPath: `${origin}${base}/tesseract/lang`,
    corePath: `${origin}${base}/tesseract/tesseract-core-lstm.wasm.js`,
    gzip: false,
    logger: (m: { status: string; progress?: number }) => statusListener?.(m),
    errorHandler: (e: unknown) => console.error('Tesseract worker error:', e),
  });
}

function obtenerWorker(): Promise<TWorker> {
  if (!workerPromise) {
    workerPromise = crearWorker().catch((e) => {
      workerPromise = null;
      throw e;
    });
  }
  return workerPromise;
}

async function descartarWorker() {
  const p = workerPromise;
  workerPromise = null;
  try {
    (await p)?.terminate();
  } catch {
    /* ya estaba muerto */
  }
}

// ---------- Utilidades de imagen ----------

function aCanvas(
  source: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  targetW: number
): HTMLCanvasElement {
  const esc = Math.min(2, targetW / sw); // permite agrandar recortes chicos hasta 2x
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * esc));
  c.height = Math.max(1, Math.round(sh * esc));
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, c.width, c.height);
  return c;
}

// Escala de grises + estiramiento de contraste (percentiles 5–95)
function preprocesar(c: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  const img = ctx.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  const grises = new Uint8ClampedArray(d.length / 4);
  const hist = new Uint32Array(256);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    grises[j] = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
    hist[grises[j]]++;
  }
  const total = grises.length;
  let lo = 0, hi = 255, acc = 0;
  for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= total * 0.05) { lo = v; break; } }
  acc = 0;
  for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc >= total * 0.05) { hi = v; break; } }
  const rango = Math.max(1, hi - lo);
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    const g = ((grises[j] - lo) * 255) / rango;
    d[i] = d[i + 1] = d[i + 2] = g;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

function canvasABlob(c: HTMLCanvasElement, calidad = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo procesar la imagen'))), 'image/jpeg', calidad)
  );
}

function cargarImagen(file: File): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(aCanvas(img, 0, 0, img.naturalWidth, img.naturalHeight, 1600));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error leyendo la foto'));
    };
    img.src = url;
  });
}

// ---------- Cámara con guía de encuadre ----------

function CamaraPatente({
  onCapture,
  onCancel,
  onError,
}: {
  onCapture: (full: HTMLCanvasElement, recorte: HTMLCanvasElement) => void;
  onCancel: () => void;
  onError: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const guiaRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [listo, setListo] = useState(false);
  const [torch, setTorch] = useState(false);
  const [tieneTorch, setTieneTorch] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();
        setListo(true);
        const caps = (stream.getVideoTracks()[0].getCapabilities?.() ?? {}) as { torch?: boolean };
        if (caps.torch) setTieneTorch(true);
      } catch (e) {
        console.error('getUserMedia:', e);
        if (!cancelado) onError();
      }
    })();
    return () => {
      cancelado = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function alternarLinterna() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      // torch no está en los tipos estándar de MediaTrackConstraints
      await track.applyConstraints({ advanced: [{ torch: !torch } as unknown as MediaTrackConstraintSet] });
      setTorch(!torch);
    } catch {
      /* sin soporte */
    }
  }

  function capturar() {
    const video = videoRef.current;
    const guia = guiaRef.current;
    if (!video || !guia || !video.videoWidth) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const vr = video.getBoundingClientRect();
    const gr = guia.getBoundingClientRect();

    // El video se muestra con object-fit: cover — mapear coords de pantalla a intrínsecas
    const esc = Math.max(vr.width / vw, vr.height / vh);
    const offX = (vw * esc - vr.width) / 2;
    const offY = (vh * esc - vr.height) / 2;

    // Margen alrededor de la guía por si el encuadre no fue exacto
    const mx = gr.width * 0.15;
    const my = gr.height * 0.3;
    const sx = Math.max(0, (gr.left - vr.left - mx + offX) / esc);
    const sy = Math.max(0, (gr.top - vr.top - my + offY) / esc);
    const sw = Math.min(vw - sx, (gr.width + mx * 2) / esc);
    const sh = Math.min(vh - sy, (gr.height + my * 2) / esc);

    const full = aCanvas(video, 0, 0, vw, vh, 1600);
    const recorte = aCanvas(video, sx, sy, sw, sh, 1000);
    onCapture(full, recorte);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6">
        <p className="text-white text-sm font-semibold mb-3 drop-shadow-md">
          Encuadrá la patente en el recuadro
        </p>
        <div
          ref={guiaRef}
          className="border-2 border-white rounded-lg"
          style={{ width: '85%', maxWidth: 420, aspectRatio: '3.1 / 1', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }}
        />
      </div>

      <div className="absolute bottom-0 left-0 right-0 pb-8 pt-4 flex items-center justify-center gap-10">
        <button
          type="button"
          onClick={onCancel}
          className="w-11 h-11 rounded-full bg-white/20 text-white text-lg flex items-center justify-center"
          aria-label="Cancelar"
        >✕</button>
        <button
          type="button"
          onClick={capturar}
          disabled={!listo}
          className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 disabled:opacity-40 shadow-lg"
          aria-label="Capturar"
        />
        {tieneTorch ? (
          <button
            type="button"
            onClick={alternarLinterna}
            className={`w-11 h-11 rounded-full text-lg flex items-center justify-center ${torch ? 'bg-yellow-400' : 'bg-white/20'}`}
            aria-label="Linterna"
          >🔦</button>
        ) : (
          <div className="w-11 h-11" />
        )}
      </div>
    </div>
  );
}

// ---------- Componente principal ----------

export default function OCRPatente({ value, onChange, onPhoto, onDeletePhoto }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [camara, setCamara] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [ocrErr, setOcrErr] = useState<string | null>(null);

  function normalizar(s: string): string {
    return s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  }

  function abrirEscaner() {
    setOcrErr(null);
    if (typeof navigator.mediaDevices?.getUserMedia === 'function') setCamara(true);
    else fileInput.current?.click();
  }

  async function conTimeout<T>(p: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const t = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        descartarWorker();
        reject(new Error('timeout'));
      }, TIMEOUT_MS);
    });
    try {
      return await Promise.race([p, t]);
    } finally {
      clearTimeout(timer);
    }
  }

  // recorte: zona de la guía (o imagen completa si vino de archivo). full: cuadro entero como último intento.
  async function reconocer(recorte: HTMLCanvasElement, full: HTMLCanvasElement | null): Promise<string | null> {
    const worker = await obtenerWorker();
    const recorteBlob = await canvasABlob(preprocesar(recorte));

    // 1) recorte de la guía como línea única
    await worker.setParameters({
      tessedit_char_whitelist: WHITELIST,
      tessedit_pageseg_mode: PSM.SINGLE_LINE,
    });
    let r = await worker.recognize(recorteBlob);
    let pat = extraerPatente(r.data.text);
    if (pat) return pat;

    // 2) recorte como texto disperso
    await worker.setParameters({ tessedit_pageseg_mode: PSM.SPARSE_TEXT });
    r = await worker.recognize(recorteBlob);
    pat = extraerPatente(r.data.text);
    if (pat) return pat;

    // 3) último intento: cuadro completo reducido
    if (full) {
      const fullBlob = await canvasABlob(preprocesar(aCanvas(full, 0, 0, full.width, full.height, 1280)));
      r = await worker.recognize(fullBlob);
      pat = extraerPatente(r.data.text);
    }
    return pat;
  }

  async function procesar(full: HTMLCanvasElement, recorte: HTMLCanvasElement, fotoBlob?: Blob) {
    setScanning(true);
    setOcrErr(null);
    setStatus('Preparando…');

    statusListener = (m) => {
      if (m.status === 'loading tesseract core') setStatus('Cargando motor…');
      else if (m.status === 'loading language traineddata') setStatus('Cargando idioma…');
      else if (m.status === 'initializing api') setStatus('Iniciando…');
      else if (m.status === 'recognizing text') {
        const p = typeof m.progress === 'number' ? Math.round(m.progress * 100) : 0;
        setStatus(`Leyendo ${p}%…`);
      }
    };

    try {
      const blob = fotoBlob ?? (await canvasABlob(full, 0.85));
      setPreview(full.toDataURL('image/jpeg', 0.8));
      onPhoto?.(blob);

      const pat = await conTimeout(reconocer(recorte, recorte === full ? null : full));
      if (pat) onChange(pat);
      else setOcrErr('No se detectó la patente — ingresala manualmente.');
    } catch (e: unknown) {
      console.error('OCR error:', e);
      if (e instanceof Error && e.message === 'timeout') {
        let wasmCached = false;
        try {
          const base = (import.meta.env.BASE_URL as string).replace(/\/$/, '');
          const corePath = `${window.location.origin}${base}/tesseract/tesseract-core-lstm.wasm.js`;
          for (const name of await caches.keys()) {
            if (await (await caches.open(name)).match(corePath)) { wasmCached = true; break; }
          }
        } catch { /* caches API no disponible */ }
        setOcrErr(
          wasmCached
            ? 'Tiempo agotado. Si el problema persiste, recargá la página una vez.'
            : 'Tiempo agotado. Necesitás WiFi la primera vez para bajar el motor OCR.'
        );
      } else {
        let msg = 'Error desconocido';
        if (typeof e === 'string') msg = e;
        else if (e instanceof Error && e.message) msg = e.message;
        else if (e && typeof e === 'object' && 'type' in e) msg = `Evento: ${(e as { type: string }).type}`;
        else { try { msg = JSON.stringify(e); } catch { msg = String(e); } }
        setOcrErr(`Error OCR: ${msg}`);
      }
    } finally {
      statusListener = null;
      setScanning(false);
      setStatus('');
    }
  }

  async function escanearArchivo(file: File) {
    // Fallback sin cámara en vivo: sin guía de recorte, se usa la imagen completa
    try {
      const full = await cargarImagen(file);
      await procesar(full, full, file);
    } catch (e) {
      setOcrErr(e instanceof Error ? e.message : 'Error leyendo la foto');
    }
  }

  return (
    <div className="space-y-3">
      {camara && (
        <CamaraPatente
          onCapture={(full, recorte) => {
            setCamara(false);
            procesar(full, recorte);
          }}
          onCancel={() => setCamara(false)}
          onError={() => {
            setCamara(false);
            fileInput.current?.click();
          }}
        />
      )}

      <button
        type="button"
        onClick={abrirEscaner}
        disabled={scanning}
        className="w-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 border border-gray-300 rounded-xl py-3 px-4 text-sm font-semibold text-gray-700 flex items-center justify-center gap-2"
      >
        📷 {scanning ? status || 'Procesando…' : 'Escanear con cámara'}
      </button>

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) escanearArchivo(f);
          e.target.value = '';
        }}
      />

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Patente</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(normalizar(e.target.value))}
          placeholder="AB123CD"
          className="w-full bg-white border-2 border-gray-300 rounded-xl px-3 py-3 text-2xl font-mono tracking-wider text-center uppercase text-gray-900 focus:border-brand-500 outline-none"
          maxLength={10}
        />
        <p className="text-xs text-gray-400 mt-1">
          El OCR es orientativo — verificá siempre antes de confirmar.
        </p>
      </div>

      {ocrErr && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ⚠️ {ocrErr}
        </p>
      )}

      {preview && (
        <div className="relative">
          <img
            src={preview}
            alt="Foto de la patente"
            className="w-full max-h-48 object-cover rounded-xl border border-gray-200"
          />
          <button
            type="button"
            onClick={() => {
              setPreview(null);
              onDeletePhoto?.();
            }}
            className="absolute top-2 right-2 bg-red-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
            aria-label="Eliminar foto"
          >✕</button>
        </div>
      )}
    </div>
  );
}
